import { useCallback, useContext, useEffect, useRef, useState } from "react";

import { getMyConversation, listStaffConversations } from "../api/chatService";
import { UserContext } from "../UserContext";
import {
   getChatToastContext,
   subscribeChatToastContext,
} from "../stores/chatToastContext";
import { markChatUnread } from "../stores/chatUnreadStore";
import {
   buildChatToastItem,
   isIncomingChatToastMessage,
   isStaffRole,
} from "../utils/chatMessageFormat";
import useChatSocket from "./useChatSocket";

const MAX_QUEUE = 5;

export default function useChatMessageToasts() {
   const { user, loading } = useContext(UserContext);
   const canAccess = Boolean(user?.id);

   const [queue, setQueue] = useState([]);
   const seenRef = useRef(new Set());
   const initializedRef = useRef(false);
   const contextRef = useRef(getChatToastContext());
   const conversationByIdRef = useRef(new Map());

   const enqueue = useCallback((items) => {
      setQueue((prev) => {
         const next = [...prev];
         for (const item of items) {
            const id = String(item?.messageId ?? "");
            if (!id || seenRef.current.has(id)) continue;
            seenRef.current.add(id);
            if (!next.some((x) => String(x.messageId) === id)) {
               next.push(item);
            }
         }
         return next.slice(-MAX_QUEUE);
      });
   }, []);

   const dismissCurrent = useCallback(() => {
      setQueue((prev) => prev.slice(1));
   }, []);

   useEffect(() => {
      contextRef.current = getChatToastContext();
      return subscribeChatToastContext(() => {
         contextRef.current = getChatToastContext();
      });
   }, []);

   useEffect(() => {
      if (!canAccess || loading) return undefined;

      let cancelled = false;

      (async () => {
         try {
            if (isStaffRole(user?.role)) {
               const conversations = await listStaffConversations();
               if (cancelled) return;
               const map = new Map();
               for (const conv of conversations || []) {
                  if (conv?.id != null) {
                     map.set(String(conv.id), conv);
                  }
                  for (const msg of conv?.messages || []) {
                     if (msg?.id != null) seenRef.current.add(String(msg.id));
                  }
                  if (conv?.lastMessage?.id != null) {
                     seenRef.current.add(String(conv.lastMessage.id));
                  }
               }
               conversationByIdRef.current = map;
            } else {
               const conversation = await getMyConversation();
               if (cancelled) return;
               for (const msg of conversation?.messages || []) {
                  if (msg?.id != null) seenRef.current.add(String(msg.id));
               }
            }
         } catch {
            // silent
         } finally {
            if (!cancelled) initializedRef.current = true;
         }
      })();

      return () => {
         cancelled = true;
      };
   }, [canAccess, loading, user?.role]);

   useChatSocket(canAccess && !loading, (message) => {
      if (!initializedRef.current || !message?.id) return;
      if (!isIncomingChatToastMessage(message, user)) return;

      const id = String(message.id);
      if (seenRef.current.has(id)) return;

      const ctx = contextRef.current;
      if (
         ctx.popupOpen &&
         ctx.conversationId != null &&
         Number(ctx.conversationId) === Number(message.conversationId)
      ) {
         seenRef.current.add(id);
         return;
      }

      if (isStaffRole(user?.role) && message.conversationId != null) {
         const key = String(message.conversationId);
         if (!conversationByIdRef.current.has(key)) {
            conversationByIdRef.current.set(key, {
               id: message.conversationId,
               student: message.senderId
                  ? {
                       id: message.senderId,
                       firstName: safeSplitName(message.senderName).firstName,
                       lastName: safeSplitName(message.senderName).lastName,
                    }
                  : null,
            });
         }
      }

      enqueue([
         buildChatToastItem(message, {
            user,
            conversationById: conversationByIdRef.current,
         }),
      ]);

      if (!isStaffRole(user?.role)) {
         markChatUnread();
      }
   });

   const current = queue[0] ?? null;

   return { canAccess, current, dismissCurrent, queueLength: queue.length };
}

function safeSplitName(value) {
   const parts = String(value ?? "").trim().split(/\s+/).filter(Boolean);
   if (!parts.length) return { firstName: "", lastName: "" };
   if (parts.length === 1) return { firstName: parts[0], lastName: "" };
   return {
      firstName: parts[0],
      lastName: parts.slice(1).join(" "),
   };
}
